import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, RefreshCw, Truck } from "lucide-react";
import { Button, EmptyState, Skeleton, StatusBadge } from "@finora/ui";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

const TONE: Record<string, "success" | "warning" | "neutral"> = {
  DELIVERED: "success",
  DELIVERY_FAILED: "warning",
  OUT_FOR_DELIVERY: "warning",
};

function fmt(iso?: string | null) {
  return iso ? new Date(iso).toLocaleString("sr-RS", { dateStyle: "short", timeStyle: "short" }) : "-";
}

/** Praćenje pošiljki: sve poslate pošiljke sa trenutnim statusom i istorijom (06.10.2026). */
export function ShipmentTrackingTab() {
  const queryClient = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ["shipments-all", showAll],
    queryFn: () => api.listAllShipments(showAll),
    retry: false,
  });

  const refresh = useMutation({
    mutationFn: (id?: string) => api.refreshShipments(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["shipments-all"] }),
  });

  if (loadError) {
    return <ModuleGateNotice error={loadError} moduleKey="courier_shipping" />;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
          Prikaži i isporučene
        </label>
        <Button variant="secondary" disabled={refresh.isPending} onClick={() => refresh.mutate(undefined)}>
          <RefreshCw className={`mr-1.5 h-4 w-4 ${refresh.isPending ? "animate-spin" : ""}`} aria-hidden />
          Osveži statuse
        </Button>
      </div>
      <p className="text-xs text-slate-400">
        Statusi se sami osvežavaju u 00:00 i 12:00 i kad kurir javi promenu (webhook); dugme osvežava odmah.
      </p>
      {refresh.isError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {refresh.error instanceof Error ? refresh.error.message : "Osvežavanje nije uspelo."}
        </p>
      )}

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : !data || data.length === 0 ? (
        <EmptyState icon={Truck} title="Nema pošiljki" description="Pošiljke koje pošalješ kuriru iz naloga magacinu pojaviće se ovde." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-500">
              <tr>
                <th className="px-3 py-2">Poslato</th>
                <th className="px-3 py-2">Kurir</th>
                <th className="px-3 py-2">Nalog / kupac</th>
                <th className="px-3 py-2">Broj za praćenje</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {data.map((s) => (
                <>
                  <tr key={s.id} className="border-t border-border align-top">
                    <td className="px-3 py-2 whitespace-nowrap">{fmt(s.createdAt)}</td>
                    <td className="px-3 py-2">{s.courierLabel}</td>
                    <td className="px-3 py-2">
                      <div>{s.orderNumber ?? "-"}</div>
                      <div className="text-xs text-slate-500">{s.customerName ?? ""}</div>
                    </td>
                    <td className="px-3 py-2">
                      {s.trackingUrl ? (
                        <a href={s.trackingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand underline">
                          {s.trackingNumber} <ExternalLink className="h-3 w-3" aria-hidden />
                        </a>
                      ) : (
                        s.trackingNumber
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {s.canRefresh ? (
                        <>
                          <StatusBadge label={s.deliveryStateLabel ?? "Čeka prvi status"} tone={TONE[s.deliveryState ?? ""] ?? "neutral"} />
                          <div className="mt-1 text-xs text-slate-500">
                            {s.deliveryStatusText ? `${s.deliveryStatusText} · ` : ""}
                            {fmt(s.lastStatusAt)}
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-slate-400">Praćenje nije dostupno za ovog kurira</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      {s.canRefresh && (
                        <>
                          <button className="mr-3 text-xs text-brand underline" onClick={() => setOpen(open === s.id ? null : s.id)}>
                            Istorija
                          </button>
                          <button
                            className="text-xs text-brand underline"
                            disabled={refresh.isPending}
                            onClick={() => refresh.mutate(s.id)}
                          >
                            Osveži
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  {open === s.id && (
                    <tr key={`${s.id}-h`} className="bg-slate-50">
                      <td colSpan={6} className="px-3 py-2">
                        {s.events && s.events.length > 0 ? (
                          <ul className="space-y-1 text-xs text-slate-600">
                            {s.events.map((e, i) => (
                              <li key={i}>
                                <span className="text-slate-400">{fmt(e.occurredAt)}</span> - {e.description || e.code}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-slate-400">Još nema statusa od kurira - klikni "Osveži".</p>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
